import {
  tablesDB,
  DATABASE_ID,
  DRIVERS_TABLE_ID,
  VEHICLES_TABLE_ID,
  USERS_TABLE_ID,
  Query,
  ID,
} from '../lib/appwrite';
import { createAuthUser } from './adminUserService';

/**
 * Get all drivers with their vehicle info joined.
 */
export async function getDriversWithDetails() {
  const [driversRes, vehiclesRes] = await Promise.all([
    tablesDB.listRows(DATABASE_ID, DRIVERS_TABLE_ID, [
      Query.limit(2000),
      Query.orderDesc('$createdAt'),
    ]),
    tablesDB.listRows(DATABASE_ID, VEHICLES_TABLE_ID, [Query.limit(2000)]),
  ]);

  const drivers = driversRes.rows || [];
  const vehicles = vehiclesRes.rows || [];

  // Build maps for quick lookup
  const vehicleByDriverId = {};
  vehicles.forEach((v) => {
    if (v.driverId) vehicleByDriverId[v.driverId] = v;
  });

  const vehicleFromDriverRow = (d) => {
    const reg = d.vehicleRegNumber;
    if (!reg && !d.vehicleModelName && !d.vehicleLatraLicenceUrl && !d.vehicleRegDocUrl) {
      return null;
    }
    return {
      registrationNumber: reg || '',
      modelName: d.vehicleModelName || '',
      latraLicenceDocUrl: d.vehicleLatraLicenceUrl || '',
      vehicleRegistrationDocUrl: d.vehicleRegDocUrl || '',
    };
  };

  return drivers.map((d) => ({
    ...d,
    vehicle: vehicleByDriverId[d.driverId] || vehicleFromDriverRow(d),
  }));
}

/**
 * Approve a driver by setting isVerified = true.
 */
export async function approveDriver(driverDocId) {
  return tablesDB.updateRow(DATABASE_ID, DRIVERS_TABLE_ID, driverDocId, {
    isVerified: true,
  });
}

/**
 * Reject / suspend a driver by setting isVerified = false.
 */
export async function rejectDriver(driverDocId) {
  return tablesDB.updateRow(DATABASE_ID, DRIVERS_TABLE_ID, driverDocId, {
    isVerified: false,
  });
}

/**
 * Create a driver directly from the admin panel: Auth account (so they can
 * log into Driver_app), a `users` row already past the registration wizard
 * (registrationStep 3 / 'completed', matching what the wizard itself writes
 * on its last step — see Driver_app's vehicleregistration.dart), and a
 * `drivers` row with isVerified: true. Unlike self-registration, this skips
 * the document-review queue entirely — the admin vouches for the driver.
 * Requires REACT_APP_APPWRITE_API_KEY with users.write scope (same as createAdminUser).
 */
export async function createDriver(data) {
  const driverId = ID.unique();
  const name = [data.fname, data.lname].filter(Boolean).join(' ').trim() || data.email;
  const authUser = await createAuthUser({
    userId: driverId,
    email: data.email?.trim(),
    password: data.password,
    name: name || undefined,
    phone: data.phoneNumber?.trim() || undefined,
  });
  const uid = authUser.$id || driverId;

  await tablesDB.createRow(DATABASE_ID, USERS_TABLE_ID, uid, {
    uid,
    email: data.email || '',
    fname: data.fname || '',
    lname: data.lname || '',
    phoneNumber: data.phoneNumber || '',
    role: 'driver',
    roles: 'driver',
    registrationStep: 3,
    registrationStatus: 'completed',
  }, null, true);

  const driverRow = await tablesDB.createRow(DATABASE_ID, DRIVERS_TABLE_ID, ID.unique(), {
    driverId: uid,
    fname: data.fname || '',
    lname: data.lname || '',
    phoneNumber: data.phoneNumber || '',
    nidaNumber: data.nidaNumber || '',
    passportPicUrl: '',
    licencePicUrl: '',
    policeClearenceUrl: '',
    isVerified: true,
  }, null, true);

  if (data.vehicleRegistrationNumber?.trim() || data.vehicleModelName?.trim()) {
    await tablesDB.createRow(DATABASE_ID, VEHICLES_TABLE_ID, ID.unique(), {
      driverId: uid,
      registrationNumber: data.vehicleRegistrationNumber?.trim() || '',
      modelName: data.vehicleModelName?.trim() || '',
      latraLicenceDocUrl: '',
      vehicleRegistrationDocUrl: '',
    }, null, true);
  }

  return driverRow;
}
